import { tiffPreviewSize, toRgba8 } from './imageView';

// Browsers other than Safari cannot draw a TIFF, so the Image Viewer decodes
// it (geotiff, loaded only when a TIFF is opened) to RGBA pixels it can paint.

export interface DecodedImage { rgba: Uint8ClampedArray<ArrayBuffer>; width: number; height: number; fullWidth: number; fullHeight: number }

const maxOf = (data: ArrayLike<number>): number => {
  let max = 0;
  for (let i = 0; i < data.length; i++) if (data[i] > max) max = data[i];
  return max;
};

/** The first page of a TIFF as RGBA bytes, scaled down to a size a canvas can hold. */
export async function decodeTiff(buffer: ArrayBuffer): Promise<DecodedImage> {
  const { fromArrayBuffer } = await import('geotiff');
  const tiff = await fromArrayBuffer(buffer);
  const image = await tiff.getImage();
  const fullWidth = image.getWidth();
  const fullHeight = image.getHeight();
  const size = tiffPreviewSize(fullWidth, fullHeight);
  const data = await image.readRGB({ interleave: true, width: size.width, height: size.height, enableAlpha: true });
  const pixels = size.width * size.height;
  const samples = Math.max(1, Math.round(data.length / pixels));
  // 8-bit stays as it is; 16-bit and float samples are stretched by their own range.
  const maxValue = data instanceof Uint8Array || data instanceof Uint8ClampedArray ? 255
    : data instanceof Uint16Array ? 65535
    : maxOf(data) || 1;
  return { rgba: toRgba8(data, pixels, samples, maxValue), width: size.width, height: size.height, fullWidth, fullHeight };
}

/** A decoded image as a blob an <img> can show: JPEG when it is opaque, PNG to keep transparency. */
export async function decodedToBlob(img: DecodedImage): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot draw the image');
  ctx.putImageData(new ImageData(img.rgba, img.width, img.height), 0, 0);
  let opaque = true;
  for (let i = 3; i < img.rgba.length; i += 4) if (img.rgba[i] < 255) { opaque = false; break; }
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The image could not be prepared'))), opaque ? 'image/jpeg' : 'image/png', 0.92);
  });
}
